// frontend/src/utils/funnel.js
//
// Sign-up source and funnel tracking for the name-generator pages (Growth
// round, T25). The admin page's 'Sign-ups by source' reads what this sends.
//
// FIRST TOUCH. The first time a visitor opens a generator page, this browser
// remembers that page's slug and where they came from (FIRST_TOUCH_KEY):
// UTM source / medium / campaign if the link had them, else the referring
// site's name, else 'direct'. It is kept for 90 days and is not replaced by
// later visits. The sign-up form sends it as `signupPage` / `signupSource`,
// and the server keeps it on the new account (users.signup_page / _source).
//
// EVENTS. page_view, names_generated, claim_click, ai_vibe_used,
// kit_unlock_click, signup_started and signup_completed go to our own
// POST /api/public/events with the page slug, the first-touch source and the
// anonymous browser id from publicAi.js — nothing that identifies a person.
// They also go to Google Analytics (gtag, already on every page) except
// page_view, which App.js already sends there.
//
// Sending never throws and never blocks a click. Under Jest nothing is sent
// unless a test installs a sender with setFunnelSender().

import { getBrowserId } from './publicAi'

export const FIRST_TOUCH_KEY = 'gs_first_touch'
export const EVENTS_PATH = '/api/public/events'
export const FUNNEL_EVENTS = [
  'page_view', 'names_generated', 'claim_click', 'ai_vibe_used',
  'kit_unlock_click', 'signup_started', 'signup_completed',
]

const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000
const SLUG = /^[a-z0-9][a-z0-9-]{0,79}$/
const PART_MAX = 40
const SOURCE_MAX = 120

const part = v => String(v || '').toLowerCase().replace(/[^a-z0-9._+-]/g, '').slice(0, PART_MAX)

/**
 * Where this visit came from:
 *   ?utm_source=google&utm_medium=cpc&utm_campaign=spring → 'google / cpc / spring'
 *   no UTM, referred by https://www.google.com/          → 'google.com'
 *   no UTM, no referrer (or our own site)                → 'direct'
 */
export function sourceFrom (search, referrer, ownHost) {
  const q = new URLSearchParams(search || '')
  const utm = ['utm_source', 'utm_medium', 'utm_campaign'].map(k => part(q.get(k))).filter(Boolean)
  if (utm.length) return utm.join(' / ').slice(0, SOURCE_MAX)
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '')
    const own = String(ownHost || '').toLowerCase().replace(/^www\./, '')
    if (host && host !== own) return part(host) || 'direct'
  } catch { /* no referrer, or not a URL */ }
  return 'direct'
}

function readJson (key) {
  try { return JSON.parse(window.localStorage.getItem(key) || 'null') } catch { return null }
}

/** { page, source, at } or null (none yet, expired, or storage blocked). */
export function readFirstTouch () {
  const t = readJson(FIRST_TOUCH_KEY)
  if (!t || typeof t !== 'object' || !SLUG.test(String(t.page || ''))) return null
  if (!(Date.now() - Number(t.at) < FIRST_TOUCH_TTL_MS)) return null
  return { page: t.page, source: typeof t.source === 'string' && t.source ? t.source : 'direct', at: Number(t.at) }
}

/** On a generator page: remember it unless an earlier visit already did. */
export function captureFirstTouch (page, { search, referrer, ownHost } = {}) {
  const existing = readFirstTouch()
  if (existing || !SLUG.test(String(page || ''))) return existing
  const loc = typeof window !== 'undefined' ? window.location : {}
  const touch = {
    page,
    source: sourceFrom(
      search !== undefined ? search : loc.search,
      referrer !== undefined ? referrer : (typeof document !== 'undefined' ? document.referrer : ''),
      ownHost !== undefined ? ownHost : loc.hostname,
    ),
    at: Date.now(),
  }
  try { window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch)) } catch { /* storage blocked */ }
  return touch
}

export function clearFirstTouch () {
  try { window.localStorage.removeItem(FIRST_TOUCH_KEY) } catch { /* storage blocked */ }
}

/**
 * The sign-up's source. The first touch wins; a sign-up link from a
 * generator page (?from=<slug>) stands in when this browser kept none.
 */
export function signupSourceFrom (from) {
  const touch = readFirstTouch()
  if (touch) return { page: touch.page, source: touch.source }
  return SLUG.test(String(from || '')) ? { page: from, source: 'direct' } : null
}

/** The extra register fields: { signupPage, signupSource } or {}. */
export function signupPayload (from) {
  const s = signupSourceFrom(from)
  return s ? { signupPage: s.page, signupSource: s.source } : {}
}

/* ── sending ─────────────────────────────────────────────────────────── */

function defaultSender (payload) {
  if (typeof fetch !== 'function') return
  // keepalive lets a click that leaves the page still deliver the event.
  fetch(EVENTS_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {})
}

let sender = process.env.NODE_ENV === 'test' ? null : defaultSender

/** Tests: setFunnelSender(payload => …); setFunnelSender(null) turns sending off. */
export function setFunnelSender (fn) { sender = fn }

/**
 * Record one funnel step for a generator page. `page` defaults to the
 * first-touch page (the sign-up steps). Does nothing without a page.
 */
export function trackFunnel (event, page) {
  try {
    if (!FUNNEL_EVENTS.includes(event)) return
    const touch = readFirstTouch()
    const slug = page || (touch && touch.page)
    if (!SLUG.test(String(slug || ''))) return
    const source = touch ? touch.source : 'direct'
    if (sender) sender({ event, page: slug, source, browserId: getBrowserId() })
    if (event !== 'page_view' && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', event, { funnel_page: slug, funnel_source: source })
    }
  } catch { /* tracking must never break the page */ }
}
